import { useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { sendAccountMessage } from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";
import { TextField } from "@/components/TextField";

// A broker "message us" form on the account page (BRK-S159, pairs PMS-S356). The
// message is recorded in Signal's Communication Log — durable, account-scoped, and
// answered by our team within an SLA — deliberately DISTINCT from the Crisp live
// chat widget (BRK-S136), which is for quick, ephemeral questions. Client
// validation mirrors the PMS schema (subject/body required, bounded); the server
// fixes source=broker / direction=inbound / medium=portal.

const SUBJECT_MAX = 200;
const BODY_MAX = 5000;

export function AccountMessageForm({ accountId }: { accountId: string }) {
  const { authedRequest } = useAuth();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentNumber, setSentNumber] = useState<string | null>(null);

  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const valid =
    trimmedSubject.length > 0 &&
    trimmedSubject.length <= SUBJECT_MAX &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= BODY_MAX;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const receipt = await sendAccountMessage(authedRequest, accountId, {
        subject: trimmedSubject,
        body: trimmedBody,
      });
      setSentNumber(receipt.communicationNumber);
      setSubject("");
      setBody("");
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold">Message us about this account</h2>
        <p className="text-sm text-brk-dim">
          Send our team a message about this account — we&rsquo;ll follow up and
          keep it on the account&rsquo;s record. For a quick question, use the
          chat button instead.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {sentNumber && (
        <p role="status" className="text-sm text-green-700">
          ✓ Message sent ({sentNumber}). We&rsquo;ll be in touch.
        </p>
      )}

      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <TextField
          label="Subject"
          value={subject}
          maxLength={SUBJECT_MAX}
          onChange={(e) => {
            setSubject(e.target.value);
            setSentNumber(null);
          }}
          required
        />
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-brk-text">Message</span>
          <textarea
            aria-label="Message"
            value={body}
            rows={4}
            maxLength={BODY_MAX}
            onChange={(e) => {
              setBody(e.target.value);
              setSentNumber(null);
            }}
            className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
          />
        </label>
        <button
          type="submit"
          disabled={busy || !valid}
          className="self-start rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Sending…" : "Send message"}
        </button>
      </form>
    </section>
  );
}
